import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ObjectSchema, object, string } from 'yup'

import {
  checkboxSchema,
  emailSchema,
  nonEmptyPositiveNumberSchema,
  nonEmptyStringSchema,
  phoneSchema,
  stringSchema,
  urlSchema,
  useMetaResponseHandler,
} from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { StoresContract } from '@/Store/application'
import { FoodTypeModel, StoresRepository } from '@/Store/domain'
import { storesTranslation } from '@/Store/ui/storesTranslation'
import { MerchantsDataModel } from '@/User/domain'

export interface StoreForm {
  merchantName: string
  afiliateCode: number
  addressEmail?: string
  storePhone?: string
  merchantWebUrl?: string

  merchantCoockTypeId: number | null
  merchantCoockType?: string

  merchantIsOnlineStore?: boolean
  merchantHasHomeDelivery?: boolean
  merchantHasLocalPickup?: boolean
  service?: string

  street: string
  number: string
  floorBlockStair?: string
  postalCode: string
  city: string
  province: string

  latitude: string | null
  longitude: string | null

  additionalInformation?: string

  addressId: number
}

const schema = (t: TFunction<'translation', undefined>): ObjectSchema<StoreForm> =>
  object({
    merchantName: nonEmptyStringSchema(t, 50),
    afiliateCode: nonEmptyPositiveNumberSchema(t),
    addressEmail: emailSchema(t, true),
    storePhone: phoneSchema(t),
    merchantWebUrl: urlSchema(t, false),

    merchantCoockTypeId: nonEmptyPositiveNumberSchema(t),
    merchantCoockType: stringSchema(t),

    merchantIsOnlineStore: checkboxSchema(t),
    merchantHasHomeDelivery: checkboxSchema(t),
    merchantHasLocalPickup: checkboxSchema(t),
    service: string(),

    street: nonEmptyStringSchema(t),
    number: nonEmptyStringSchema(t),
    floorBlockStair: stringSchema(t),
    postalCode: nonEmptyStringSchema(t),
    city: nonEmptyStringSchema(t),
    province: nonEmptyStringSchema(t),

    latitude: nonEmptyStringSchema(t),
    longitude: nonEmptyStringSchema(t),

    additionalInformation: stringSchema(t),

    addressId: nonEmptyPositiveNumberSchema(t),
  })

export interface IUseStoreController {
  foodTypes: FoodTypeModel[]
  form: UseFormReturn<StoreForm>
  save: (data: StoreForm) => Promise<void>
}

export const useStoreController = (
  repository: StoresRepository,
  merchant: MerchantsDataModel,
  readOnly = false,
  foodTypeMode = false,
): IUseStoreController => {
  const { t } = useTranslation()
  const { startLoading, stopLoading } = useLoader()

  const [foodTypes, setFoodTypes] = useState<FoodTypeModel[]>([])
  const { handleMetaResponse } = useMetaResponseHandler()

  const form = useForm<StoreForm>({
    resolver: yupResolver(schema(t)),
    mode: 'onChange',
  })

  const load = async (id: number): Promise<void> => {
    startLoading()

    const response = !merchant.isNursery ? await StoresContract(repository).getRestaurantData(id) : 
      await StoresContract(repository).getKindergartenData(id)

    stopLoading()

    if (handleMetaResponse(response?.meta, undefined, { notifySuccess: false })) {
      form.reset({
        ...form.getValues(),
        merchantName: response.data?.name ?? '',
        afiliateCode: response.data?.afiliateCode ?? 0,
        addressEmail: response.data?.email ?? '',
        storePhone: response.data?.phone ?? '',
        merchantWebUrl: response.data?.url ?? '',

        merchantCoockTypeId: response.data?.food?.id ?? (merchant.isNursery ? 0 : null),
        merchantCoockType: response.data?.food?.name ?? '',

        merchantIsOnlineStore: response.data?.service.physical ?? false,
        merchantHasHomeDelivery: response.data?.service.delivery ?? false,
        merchantHasLocalPickup: response.data?.service.takeAway ?? false,
        service: '',

        street: response.data?.street ?? '',
        number: response.data?.number ?? '',
        floorBlockStair: response.data?.floorBlockStair ?? '',
        postalCode: response.data?.postalCode ?? '',
        city: response.data?.city ?? '',
        province: response.data?.province ?? '',

        latitude: response.data?.latitude ?? null,
        longitude: response.data?.longitude ?? null,

        additionalInformation: response.data?.additionalInfo ?? '',

        addressId: response.data?.addressId ?? 0,
      })
    }
  }

  const loadFoodTypes = async (): Promise<void> => {
    startLoading()

    const response = await StoresContract(repository).getRestaurantFoodTypes()

    stopLoading()

    if (handleMetaResponse(response?.meta, undefined, { notifySuccess: false })) {
      setFoodTypes(response.data ?? [])
    }
  }

  const save = async (data: StoreForm): Promise<void> => {
    startLoading()

    if (data.latitude === null || data.longitude === null) {
      form.setError('street', {
        type: 'manual',
        message: t(storesTranslation.myStore.form.address.noPlacesError),
      })
    }

    if (
      !data.merchantIsOnlineStore &&
      !data.merchantHasHomeDelivery &&
      !data.merchantHasLocalPickup
    ) {
      form.setError('service', {
        type: 'manual',
        message: t(storesTranslation.myStore.form.data.service.noServiceError),
      })

      form.setFocus('merchantIsOnlineStore')

      stopLoading()
      return
    }

    if (data.merchantCoockTypeId !== null) {
      data.merchantCoockType =
        foodTypes.find(f => f.id === data.merchantCoockTypeId)?.name ?? ''
    }

    const response = !merchant.isNursery ? await StoresContract(repository).saveRestaurantData(data) :
      await StoresContract(repository).saveKindergartenData(data)

    stopLoading()

    handleMetaResponse(response?.meta, form, { notifySuccess: true })
  }

  useEffect(() => {
    if (!foodTypeMode && merchant?.id) {
      if (!readOnly) loadFoodTypes()
      load(merchant?.id)
    }

    if (foodTypeMode) loadFoodTypes()
  }, [merchant?.id])
  return {
    foodTypes,
    form,
    save,
  }
}
